import * as React from 'react';
import cx from 'clsx';
import { Button } from '@vechaiui/react';
import { Form, FormikProps } from 'formik';
import { useLocalStorageValue } from '@react-hookz/web';
import { useLocalization } from '@fluent/react';
import { useMutation } from '@apollo/client';

import Formik from '#/components/forms/Formik';
import LoginForm from '#/components/forms/LoginForm';
import LoginLayout from '#/layouts/LoginLayout';
import O11y from '#/utils/o11y';
import yup from '#/libs/yup';
import { AUTHENTICATE_INITIAL_VALUES } from '#/constants/form-initial-values';
import { AUTH_TOKEN_KEY, REFRESH_TOKEN_KEY } from '#/constants/auth';
import { ApiErrorBlock, ErrorBlockFormik } from '#/components/forms/ErrorBlock';
import { AuthenticateDocument } from '#/__generated__/graphql/graphql';
import { authenticateSchema } from '#/constants/validation-schema';

const LoginPage = () => {
  const [, writeAuthToken] = useLocalStorageValue(AUTH_TOKEN_KEY);
  const [, writeRefreshToken] = useLocalStorageValue(REFRESH_TOKEN_KEY);
  const [authenticateMutation, authenticateMutationResult] = useMutation(AuthenticateDocument);
  const { l10n } = useLocalization();
  const [rememberMe, setRememberMe] = React.useState(false);
  const [showLoginForm, setShowLoginForm] = React.useState(false);
  const formikRef = React.useRef<FormikProps<typeof AUTHENTICATE_INITIAL_VALUES>>(null);

  const handleSubmit = async ({ rememberMe, ...values }: typeof AUTHENTICATE_INITIAL_VALUES) => {
    setRememberMe(rememberMe);
    await authenticateMutation({ variables: { input: values } });
  };

  React.useEffect(() => {
    if (authenticateMutationResult.data?.authenticate?.accessToken) {
      writeAuthToken(authenticateMutationResult.data.authenticate.accessToken);
      O11y.setUser({ username: formikRef.current?.values.username });
    }
    if (rememberMe && authenticateMutationResult.data?.authenticate?.refreshToken) {
      writeRefreshToken(authenticateMutationResult.data.authenticate.refreshToken);
    }
  }, [authenticateMutationResult, rememberMe, writeAuthToken, writeRefreshToken]);

  return (
    <LoginLayout>
      <div className={cx(!showLoginForm && 'hidden')}>
        <Formik
          initialValues={AUTHENTICATE_INITIAL_VALUES}
          innerRef={formikRef}
          onSubmit={handleSubmit}
          validationSchema={yup.object(authenticateSchema)}
        >
          {(formikProps) => (
            <Form>
              <header className="mb-8">
                <h1 className="text-3xl font-bold">{l10n.getString('login')}</h1>
              </header>

              <LoginForm />

              <footer className="mt-8">
                <div className="space-y-6">
                  {!formikProps.isValid && formikProps.submitCount > 0 && <ErrorBlockFormik />}

                  {authenticateMutationResult.error?.message && (
                    <ApiErrorBlock errors={[authenticateMutationResult.error]} />
                  )}

                  <Button
                    className="w-full"
                    color="primary"
                    size="xl"
                    type="submit"
                    variant="solid"
                  >
                    {l10n.getString('login')}
                  </Button>
                </div>
              </footer>
            </Form>
          )}
        </Formik>
      </div>

      <div className={cx(showLoginForm && 'hidden')}>
        <header className="mb-8">
          <h1 className="text-7xl text-black">
            <p>{l10n.getString('its-time-to')}</p>
            <p className="mt-3">{l10n.getString('app-name')}</p>
            <p>{l10n.getString('forward')}</p>
          </h1>
        </header>

        <Button
          className="w-full"
          color="primary"
          onClick={() => setShowLoginForm(true)}
          size="xl"
          variant="solid"
        >
          {l10n.getString('login')}
        </Button>
      </div>
    </LoginLayout>
  );
};

export default LoginPage;
